<script setup lang="ts">
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
} from 'bootstrap-vue-next';
import { ref } from 'vue';

import { bulkIssueBonusPoints, issueBonusPoints } from '~/api/bonusPoints';
import LoadingButton from '~/common/components/LoadingButton.vue';
import { BONUS_POINT_ACTIVITY_TYPES } from '~/common/Constants';

interface PointsForm {
  usernames: string;
  reason: string;
  points: string | number | null;
  activity_type: string;
}

const props = withDefaults(defineProps<{
  userId?: number | null;
}>(), {
  userId: null,
});

const model = defineModel<boolean>();
const emit = defineEmits<{ pointsAdded: [points: PointsForm] }>();

const blankForm: PointsForm = {
  usernames: '',
  reason: '',
  points: null,
  activity_type: '',
};

const loading = ref(false);
const form = ref<PointsForm>({ ...blankForm });

const activityTypeOptions = Object.entries(BONUS_POINT_ACTIVITY_TYPES).map(
  ([value, text]) => ({ value, text }),
);

const resetForm = () => {
  form.value = { ...blankForm };
};

const addPoints = async (event: Event) => {
  event.preventDefault();
  loading.value = true;

  const award = {
    reason: form.value.reason,
    points: Number(form.value.points),
    activity_type: form.value.activity_type,
  };

  try {
    if (props.userId === null) {
      const usernames = form.value.usernames
        .split('\n')
        .map(x => x.trim())
        .filter(x => x && !x.startsWith('<!--'));

      await bulkIssueBonusPoints({ ...award, usernames });
    } else {
      await issueBonusPoints({ ...award, user_id: props.userId });
    }

    emit('pointsAdded', form.value);
    model.value = false;
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <BModal
    v-model="model"
    title="Add points"
    no-footer
    @hidden="resetForm"
  >
    <BForm @submit="addPoints">
      <BFormGroup
        v-if="userId === null"
        label="Users (one per line)"
        class="mb-3"
      >
        <BFormTextarea
          v-model="form.usernames"
          required
          data-test-id="add-points-modal-textarea-usernames"
        />
      </BFormGroup>
      <BFormGroup
        label="Points"
        class="mb-3"
      >
        <BFormInput
          v-model="form.points"
          type="number"
          required
          data-test-id="add-points-modal-input-points"
        />
      </BFormGroup>
      <BFormGroup
        label="Activity Type"
        class="mb-3"
      >
        <BFormSelect
          v-model="form.activity_type"
          :options="activityTypeOptions"
          required
          data-test-id="add-points-modal-select-activity-type"
        />
      </BFormGroup>
      <BFormGroup
        label="Reason"
        class="mb-3"
      >
        <BFormInput
          v-model="form.reason"
          required
          data-test-id="add-points-modal-input-reason"
        />
      </BFormGroup>
      <LoadingButton
        type="submit"
        class="float-end"
        variant="primary"
        :loading="loading"
      >
        Add points
      </LoadingButton>
    </BForm>
  </BModal>
</template>
